import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button, Upload, Typography, Modal } from "antd";
import {
  CheckCircleFilled,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useResumeStore } from "../../store/resumeStore";
import useViewportWidth from "../../hooks/useViewportWidth";

const { Title, Text } = Typography;

const ANALYSIS_OPTIONS = {
  fresher: {
    title: "Fresher",
    accent: "#16a34a",
    surface: "#f0fdf4",
    border: "#bbf7d0",
    description:
      "We will score internships, academic projects, certifications, leadership, and achievements more heavily than full-time work history.",
    checklist: [
      "At least one internship, project, or practical training entry",
      "Achievements with outcomes, numbers, rankings, or recognition",
      "Relevant skills, certifications, and role-focused summary",
    ],
  },
  experience: {
    title: "Experienced",
    accent: "#2563eb",
    surface: "#eff6ff",
    border: "#bfdbfe",
    description:
      "We will focus more on work history, role progression, impact, ownership, and measurable achievements.",
    checklist: [
      "Clear work experience with dates, role scope, and key wins",
      "Metrics, ownership, and business or technical impact",
      "Target-role skills and summary aligned to the job",
    ],
  },
};

function normalizeUserType(value) {
  if (value === "fresher") return "fresher";
  return value === "experience" || value === "experienced" ? "experience" : "";
}

export default function ResumeLanding() {
  const navigate = useNavigate();
  const resetResume = useResumeStore((state) => state.resetResume);
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth <= 480;
  const isTablet = viewportWidth <= 1024;
  const isCompact = viewportWidth <= 1024;
  const [analysisAudience, setAnalysisAudience] = useState(() =>
    normalizeUserType(sessionStorage.getItem("experienceLevel")),
  );
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    resetResume();
  }, [resetResume]);

  const selectedAudience = ANALYSIS_OPTIONS[analysisAudience] || null;

  const handleAudienceSelect = (audience) => {
    sessionStorage.setItem("experienceLevel", audience);
    setAnalysisAudience(audience);
    setAudienceModalOpen(false);

    if (pendingFile) {
      const fileToImport = pendingFile;
      setPendingFile(null);
      sessionStorage.setItem("uploadedFileName", fileToImport.name);
      navigate("/resume/import", { state: { file: fileToImport } });
    }
  };

  const uploadProps = {
    name: "file",
    accept: ".pdf,.txt,.doc,.docx",
    showUploadList: false,
    maxCount: 1,

    beforeUpload: (file) => {
      if (!analysisAudience) {
        setPendingFile(file);
        setAudienceModalOpen(true);
        return false;
      }

      sessionStorage.setItem("uploadedFileName", file.name);
      sessionStorage.setItem("experienceLevel", analysisAudience);
      sessionStorage.setItem("isUpload", "yes");
      navigate("/resume/import", { state: { file } });
      return false;
    },
  };

  const handleStartBuilding = (key) => {
    sessionStorage.setItem("experienceLevel", key);
    sessionStorage.setItem("isUpload", "no");
    resetResume();
    navigate("/resume/new");
  };

  const compactPremiumAccent = "#C79A58";
  const compactPremiumAccentGradient =
    "linear-gradient(135deg, #F1D09A 0%, #C79A58 52%, #A8713C 100%)";
  const compactPremiumBlue = "#2F405A";
  const desktopPremiumFont = "'Avenir Next', 'Helvetica Neue', Arial, sans-serif";
  const desktopPremiumButtonGradient =
    "linear-gradient(135deg, #D8B06A 0%, #BC8A49 56%, #8A6136 100%)";
  const desktopPremiumDarkButton = "#28354B";
  const desktopPremiumLabelColor = "#F0D2A0";
  const pageTitleGradient = "linear-gradient(90deg,#38bdf8,#a855f7)";
  const primaryButtonStyle = {
    height: 50,
    padding: "0 26px",
    fontSize: 16,
    fontWeight: isCompact ? 600 : 700,
    borderRadius: 12,
    background: isCompact ? pageTitleGradient : desktopPremiumButtonGradient,
    border: isCompact ? "none" : "1px solid rgba(232, 210, 174, 0.7)",
    color: isCompact ? "#ffffff" : "#FFF9F1",
    boxShadow: isCompact
      ? "0 18px 40px rgba(56, 189, 248, 0.18)"
      : "0 18px 40px rgba(188, 138, 73, 0.22)",
    fontFamily: isCompact ? undefined : desktopPremiumFont,
    width: isMobile ? "100%" : "auto",
  };
  const compactPrimaryButtonStyle = {
    height: 40,
    padding: "0 20px",
    fontSize: 15,
    fontWeight: 700,
    borderRadius: 20,
    background: compactPremiumAccentGradient,
    border: "none",
    color: "#1B1612",
    boxShadow: "none",
    width: "auto",
  };
  const compactSecondaryButtonStyle = {
    height: 40,
    padding: "0 20px",
    fontSize: 15,
    fontWeight: 700,
    borderRadius: 20,
    background: "#1B2A41",
    border: "1px solid rgba(91, 111, 146, 0.8)",
    color: "#F8F1E7",
    boxShadow: "none",
    width: "auto",
  };
  const panelStyle = {
    background: isCompact
      ? "linear-gradient(180deg, rgba(16, 26, 41, 0.97), rgba(25, 34, 49, 0.94))"
      : "rgba(15, 23, 42, 0.76)",
    border: isCompact
      ? "1px solid rgba(69, 83, 102, 0.62)"
      : "1px solid rgba(148, 163, 184, 0.16)",
    borderRadius: isCompact ? 28 : 24,
    boxShadow: "0 30px 80px rgba(2, 6, 23, 0.45)",
    backdropFilter: "blur(14px)",
    overflow: "hidden",
    height: "100%",
  };
  const panelBodyStyle = {
    padding: isCompact ? (isMobile ? 22 : 24) : isMobile ? 16 : isTablet ? 22 : 32,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };
  const panelLabelStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: isCompact ? "7px 12px" : "7px 12px",
    borderRadius: 999,
    background: isCompact ? "rgba(28, 38, 54, 0.96)" : "rgba(18, 30, 50, 0.78)",
    color: isCompact ? "#E6D2B0" : desktopPremiumLabelColor,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontFamily: isCompact ? undefined : desktopPremiumFont,
    width: "fit-content",
  };
  const createIconStyle = {
    width: 84,
    height: 84,
    borderRadius: "50%",
    background: "rgba(22, 163, 74, 0.14)",
    border: "1px solid rgba(74, 222, 128, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  };
  // const uploadIconStyle = {
  //   width: 84,
  //   height: 84,
  //   borderRadius: "50%",
  //   background: "rgba(96, 165, 250, 0.14)",
  //   border: "1px solid rgba(96, 165, 250, 0.18)",
  //   display: "flex",
  //   alignItems: "center",
  //   justifyContent: "center",
  //   marginBottom: 24,
  // };
  const activeAudienceStyle = selectedAudience
    ? {
        background:
          analysisAudience === "fresher"
            ? "linear-gradient(135deg, rgba(20, 83, 45, 0.55), rgba(15, 23, 42, 0.88))"
            : "linear-gradient(135deg, rgba(30, 64, 175, 0.55), rgba(15, 23, 42, 0.88))",
        border: `1px solid ${selectedAudience.border}`,
      }
    : {
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(148, 163, 184, 0.18)",
      };
  const compactAudiencePillStyle = {
    borderRadius: 999,
    background: "rgba(18, 28, 43, 0.98)",
    border: "1px solid rgba(55, 66, 85, 0.92)",
    color: "#EADBC5",
    height: 34,
    padding: "0 14px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  };

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          padding: isCompact ? "12px 12px 18px" : "20px 20px 28px",
          background: isCompact
            ? "radial-gradient(circle at 84% 12%, rgba(185, 135, 75, 0.14), transparent 24%), radial-gradient(circle at 16% 78%, rgba(92, 202, 244, 0.12), transparent 26%), linear-gradient(180deg, #0A0F18 0%, #161C28 54%, #211B1A 100%)"
            : "#020617",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 15%, rgba(56, 189, 248, 0.18), transparent 24%), radial-gradient(circle at 80% 10%, rgba(168, 85, 247, 0.18), transparent 22%), radial-gradient(circle at 50% 55%, rgba(59, 130, 246, 0.12), transparent 32%)",
            pointerEvents: "none",
          }}
        />
        <Button
          icon={<ArrowLeftOutlined />}
          type="text"
          style={{
            fontSize: 18,
            color: "#fff",
            margin: 0,
            position: "absolute",
            top: isCompact ? 8 : 12,
            left: isCompact ? 8 : 12,
            zIndex: 10,
          }}
          onClick={() => navigate("/welcome")}
        />
        <Row justify="center" style={{ position: "relative", zIndex: 1 }}>
          <Col xs={24} lg={20} xl={18}>
            <div
              style={{
                textAlign: "center",
                marginBottom: isCompact ? 28 : 40,
                paddingTop: isCompact ? 40 : 12,
                maxWidth: isCompact ? 420 : undefined,
                marginInline: "auto",
              }}
            >
              <Title
                style={{
                  fontSize: isCompact ? (isMobile ? 30 : 36) : isMobile ? 26 : isTablet ? 32 : 40,
                  lineHeight: 1.2,
                  fontWeight: 700,
                  marginBottom: 12,
                  fontFamily: isCompact ? "Georgia, 'Times New Roman', serif" : undefined,
                  background: isCompact ? "none" : pageTitleGradient,
                  WebkitBackgroundClip: isCompact ? "initial" : "text",
                  color: isCompact ? "#F8F1E7" : "transparent",
                }}
              >
                {isCompact ? "Build a refined resume flow" : "Build or Analyze Your Resume"}
              </Title>

              {!isCompact ? (
                <Text
                  style={{
                    fontSize: isMobile ? 14 : isTablet ? 16 : 18,
                    lineHeight: 1.6,
                    padding: isMobile ? "0 8px" : 0,
                    color: "#94a3b8",
                    maxWidth: 760,
                    display: "inline-block",
                  }}
                >
                  Create a polished resume from scratch or upload an existing one and let AI review it with the right fresher or experienced lens.
                </Text>
              ) : null}
            </div>

            <Row gutter={isCompact ? [18, 18] : [30, 30]}>
              <Col xs={24} lg={12}>
                <Card
                  hoverable
                  bordered={false}
                  style={{
                    ...panelStyle,
                    ...(isCompact
                      ? { border: "1px solid rgba(183, 136, 74, 0.72)" }
                      : {}),
                  }}
                  bodyStyle={panelBodyStyle}
                  onClick={() => handleStartBuilding(analysisAudience || "experience")}
                >
                  <div style={{ width: "100%", textAlign: "left" }}>
                    <div style={panelLabelStyle}>{isCompact ? "Build" : "From Scratch"}</div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      textAlign: "left",
                      height: "100%",
                    }}
                  >
                    {!isCompact ? <div style={createIconStyle}>
                      <PlusOutlined
                        style={{ fontSize: 36, color: "#4ade80" }}
                      />
                    </div> : null}

                    <Title
                      level={3}
                      style={{
                        color: "#f8fafc",
                        marginBottom: 10,
                        marginTop: isCompact ? 18 : 0,
                        fontSize: isMobile ? 24 : 28,
                        fontFamily: isCompact ? "Georgia, 'Times New Roman', serif" : undefined,
                      }}
                    >
                      {isCompact ? "Start fresh" : "Create New Resume"}
                    </Title>

                    <Text
                      style={{
                        color: isCompact ? "#BAC0CC" : "#94a3b8",
                        fontSize: isMobile ? 14 : 16,
                        lineHeight: 1.7,
                        maxWidth: isCompact ? 286 : undefined,
                      }}
                    >
                      {isCompact
                        ? "Create your resume in the builder."
                        : "Best for students, freshers, and anyone building a clean resume from zero with guided sections."}
                    </Text>

                    {!isCompact ? <div
                      style={{
                        marginTop: 22,
                        display: "grid",
                        gap: 12,
                        width: "100%",
                      }}
                    >
                      {[
                        "Start with a friendly structure",
                        "Add projects, internships, skills, and achievements",
                        "Preview and export a polished final resume",
                      ].map((item) => (
                        <div
                          key={item}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "18px 1fr",
                            gap: 10,
                            color: "#cbd5e1",
                            fontSize: 14,
                            lineHeight: 1.6,
                          }}
                        >
                          <CheckCircleFilled
                            style={{ color: "#4ade80", marginTop: 3 }}
                          />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div> : null}

                    <div
                      style={{
                        marginTop: "auto",
                        paddingTop: isCompact ? 22 : 28,
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        type="primary"
                        size="large"
                        style={isCompact ? compactPrimaryButtonStyle : primaryButtonStyle}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartBuilding(analysisAudience || "experience");
                        }}
                      >
                        {isCompact ? "Open builder" : "Start Building"}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  hoverable
                  bordered={false}
                  style={{
                    ...panelStyle,
                    ...(isCompact
                      ? { border: "1px solid rgba(75, 91, 120, 0.76)" }
                      : {}),
                  }}
                  bodyStyle={panelBodyStyle}
                >
                  <Upload.Dragger
                    {...uploadProps}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                    }}
                  >
                    <div style={{ width: "100%", textAlign: "left" }}>
                      <div style={panelLabelStyle}>{isCompact ? "Import" : "Upload Flow"}</div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        textAlign: "left",
                        minHeight: "100%",
                      }}
                    >
                      <Title
                        level={3}
                        style={{
                          color: "#f8fafc",
                          marginBottom: 10,
                          fontSize: isMobile ? 24 : 28,
                          fontFamily: isCompact ? "Georgia, 'Times New Roman', serif" : undefined,
                        }}
                      >
                        {isCompact ? "Import resume" : "Upload Existing Resume"}
                      </Title>

                      <Text
                      style={{
                          color: isCompact ? "#BAC0CC" : "#94a3b8",
                          fontSize: isMobile ? 14 : 16,
                          lineHeight: 1.7,
                          maxWidth: isCompact ? 286 : undefined,
                        }}
                      >
                        {isCompact
                          ? "Upload your resume and review the details."
                          : "Upload your current resume and we will analyze it using the experience level you choose."}
                      </Text>

                      <div
                        style={{
                          marginTop: isCompact ? "auto" : 22,
                          alignSelf: "stretch",
                          border: isCompact ? "none" : "1px dashed rgba(96, 165, 250, 0.34)",
                          borderRadius: isCompact ? 0 : 18,
                          padding: isCompact ? "20px 0 0" : 18,
                          textAlign: "left",
                          ...(isCompact ? {} : activeAudienceStyle),
                        }}
                      >
                        {!isCompact ? <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: selectedAudience?.accent || "#e2e8f0",
                            }}
                          >
                            {selectedAudience
                              ? `Analyzing as ${selectedAudience.title}`
                              : "Choose your analysis mode"}
                          </div>
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setPendingFile(null);
                              setAudienceModalOpen(true);
                            }}
                            style={{
                              borderRadius: 999,
                              background: desktopPremiumDarkButton,
                              border: "1px solid rgba(240, 210, 160, 0.24)",
                              color: "#F6E8D1",
                              fontFamily: desktopPremiumFont,
                              fontWeight: 700,
                            }}
                          >
                            {selectedAudience ? "Change" : "Choose"}
                          </Button>
                        </div> : null}

                        {!isCompact ? <div
                          style={{
                            marginTop: 8,
                            color: "#cbd5e1",
                            fontSize: 13,
                            lineHeight: 1.7,
                          }}
                        >
                          {selectedAudience
                            ? selectedAudience.description
                            : "Tell us whether you are fresher or experienced before upload so the AI uses the right scoring expectations."}
                        </div> : null}

                        {!isCompact && selectedAudience ? (
                          <div
                            style={{
                              marginTop: 14,
                              display: "grid",
                              gap: 9,
                            }}
                          >
                            {selectedAudience.checklist.map((item) => (
                              <div
                                key={item}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "16px 1fr",
                                  gap: 8,
                                  alignItems: "start",
                                  color: "#e2e8f0",
                                  fontSize: 12.5,
                                  lineHeight: 1.55,
                                }}
                              >
                                <CheckCircleFilled
                                  style={{
                                    color: selectedAudience.accent,
                                    marginTop: 2,
                                  }}
                                />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {isCompact ? (
                          <>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 10,
                                marginBottom: 12,
                              }}
                            >
                              <div style={compactAudiencePillStyle}>
                                {selectedAudience
                                  ? `Lens: ${selectedAudience.title}`
                                  : "Lens: Choose"}
                              </div>
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setPendingFile(null);
                                  setAudienceModalOpen(true);
                                }}
                                style={compactAudiencePillStyle}
                              >
                                Change Lens
                              </Button>
                            </div>
                            <div
                              style={{
                                color: "#BAC0CC",
                                fontSize: 13,
                                lineHeight: 1.6,
                              }}
                            >
                              {selectedAudience
                                ? "You can change this later."
                                : "Choose a lens before upload."}
                            </div>
                          </>
                        ) : null}
                      </div>

                      <div
                        style={{
                          marginTop: isCompact ? 20 : 24,
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Button
                          size="large"
                          style={isCompact ? compactSecondaryButtonStyle : primaryButtonStyle}
                        >
                          {isCompact ? "Import Resume" : "Upload Resume"}
                        </Button>
                      </div>
                    </div>
                  </Upload.Dragger>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <Modal
        open={audienceModalOpen}
        footer={null}
        centered
        width={isTablet ? "calc(100vw - 24px)" : 860}
        onCancel={() => {
          setAudienceModalOpen(false);
          setPendingFile(null);
        }}
      >
        <div style={{ padding: isCompact ? "10px 6px 6px" : "8px 4px 4px" }}>
          {isCompact ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                background: "#F0E5D6",
                color: "#7F5A2B",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Premium Lens
            </div>
          ) : null}
          <div
            style={{
              fontSize: isCompact ? 30 : 26,
              fontWeight: 700,
              color: "#0f172a",
              marginTop: isCompact ? 16 : 0,
              fontFamily: isCompact ? "Georgia, 'Times New Roman', serif" : undefined,
            }}
          >
            {isCompact ? "How should we analyze your resume?" : "Choose your resume analysis mode"}
          </div>
          <div
            style={{
              marginTop: 10,
              fontSize: isCompact ? 15 : 14,
              color: "#475569",
              lineHeight: 1.65,
            }}
          >
            {isCompact
              ? "A quieter, premium mobile flow with warmer surfaces and a safer explanation. You can change this lens later before or after import."
              : "Pick the profile that matches your journey. For freshers, we expect internships, projects, certifications, and achievements rather than long full-time experience."}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "1fr"
                  : isCompact
                    ? "1fr"
                    : "repeat(2, minmax(0, 1fr))",
              gap: 18,
              marginTop: 24,
            }}
          >
            {Object.entries(ANALYSIS_OPTIONS).map(([key, option]) => (
              <div
                key={key}
                style={{
                  border: isCompact
                    ? `1px solid ${key === "fresher" ? "#E6CFB0" : "#D8DDE8"}`
                    : `1px solid ${option.border}`,
                  borderRadius: isCompact ? 24 : 18,
                  background: isCompact
                    ? key === "fresher"
                      ? "#FFF8F0"
                      : "#FBFBFD"
                    : option.surface,
                  padding: isCompact ? 22 : 20,
                  boxShadow: isCompact ? "0 16px 40px rgba(15, 23, 42, 0.06)" : "none",
                }}
              >
                {isCompact ? (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "#FFFFFF",
                      color: key === "fresher" ? "#8A6A3B" : "#6B7C91",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {option.title} Lens
                  </div>
                ) : null}
                <div
                  style={{
                    fontSize: isCompact ? 24 : 22,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginTop: isCompact ? 16 : 0,
                    fontFamily: isCompact ? "Georgia, 'Times New Roman', serif" : undefined,
                  }}
                >
                  {isCompact
                    ? key === "fresher"
                      ? "Projects, learning, and trajectory first"
                      : "Impact, ownership, and progression first"
                    : option.title}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 14,
                    color: "#475569",
                    lineHeight: 1.65,
                  }}
                >
                  {option.description}
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                  {option.checklist.map((item) => (
                    <div
                      key={item}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "16px 1fr",
                        gap: 8,
                        alignItems: "start",
                        fontSize: 13,
                        color: "#334155",
                        lineHeight: 1.55,
                      }}
                    >
                      <CheckCircleFilled
                        style={{ color: option.accent, marginTop: 2 }}
                      />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <Button
                  type="primary"
                  block
                  size="large"
                  style={{
                    marginTop: isCompact ? 20 : 18,
                    height: isCompact ? 42 : undefined,
                    borderRadius: isCompact ? 18 : 12,
                    background: isCompact
                      ? key === "fresher"
                        ? compactPremiumAccentGradient
                        : compactPremiumBlue
                      : key === "fresher"
                        ? desktopPremiumButtonGradient
                        : desktopPremiumDarkButton,
                    borderColor: isCompact
                      ? key === "fresher"
                        ? compactPremiumAccent
                        : compactPremiumBlue
                      : key === "fresher"
                        ? "#B98549"
                        : desktopPremiumDarkButton,
                    color: isCompact
                      ? key === "fresher"
                        ? "#1B1612"
                        : "#ffffff"
                      : key === "fresher"
                        ? "#FFF9F1"
                        : "#F6E8D1",
                    boxShadow: "none",
                    fontWeight: 700,
                    fontFamily: isCompact ? undefined : desktopPremiumFont,
                  }}
                  onClick={() => handleAudienceSelect(key)}
                >
                  {isCompact
                    ? key === "fresher"
                      ? "Choose Fresher"
                      : "Choose Experienced"
                    : `Continue as ${option.title}`}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}
